"use client";

import { useState, useRef, useMemo, useCallback } from "react";
import { allCountries } from "country-region-data";
import { Select } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

/** The subdivision term varies by country (State / Province / Region / ...). */
export function getRegionLabel(countryCode: string): string {
  const labels: Record<string, string> = {
    US: "State",
    AU: "State",
    IN: "State",
    BR: "State",
    MX: "State",
    MY: "State",
    NG: "State",
    MM: "State",
    CA: "Province",
    GB: "County",
    IE: "County",
    JP: "Prefecture",
    CN: "Province",
    IT: "Province",
  };
  return labels[countryCode] ?? "Region";
}

type RegionTuple = [string, string]; // [name, shortCode]

interface RegionSelectProps {
  countryCode: string;
  value: string;
  onChange: (value: string) => void;
}

export function RegionSelect({
  countryCode,
  value,
  onChange,
}: RegionSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const label = getRegionLabel(countryCode).toLowerCase();

  const regions = useMemo<RegionTuple[]>(() => {
    const entry = allCountries.find((c) => c[1] === countryCode);
    return entry ? (entry[2] as RegionTuple[]) : [];
  }, [countryCode]);

  const filtered = useMemo(
    () =>
      regions.filter(([name, code]) =>
        `${name} ${code}`.toLowerCase().includes(search.toLowerCase()),
      ),
    [regions, search],
  );

  const selected = useMemo(
    () => regions.find(([name, code]) => code === value || name === value),
    [regions, value],
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, []);

  // Countries without subdivisions fall back to a free-text input.
  if (regions.length === 0) {
    return (
      <div className="relative">
        <Icon
          name="IconMap"
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-contrast/40 pointer-events-none"
        />
        <Input
          type="text"
          variant="default"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${label}`}
          className="pl-9"
        />
      </div>
    );
  }

  return (
    <Select.Root open={open} modal onOpenChange={handleOpenChange}>
      <Select.Trigger
        size="default"
        variant="default"
        className="w-full cursor-pointer justify-between"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon name="IconMap" size={15} className="text-contrast/40 shrink-0" />
          {selected ? (
            <span className="text-sm text-heading truncate">{selected[0]}</span>
          ) : (
            <span className="text-sm text-contrast/30 truncate">{`Select ${label}`}</span>
          )}
        </div>
        <Select.Icon
          size={14}
          className={cn("ml-0 opacity-50 transition-transform duration-300", open && "rotate-180")}
        />
      </Select.Trigger>

      <Select.Content align="start" className="w-[var(--radix-popover-trigger-width)]">
        <Select.Search
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
        />
        <Select.List className="max-h-56">
          {filtered.length === 0 ? (
            <Select.ListEmpty>No results.</Select.ListEmpty>
          ) : (
            filtered.map(([name, code]) => (
              <Select.Item
                key={code || name}
                selected={code === value || name === value}
                onClick={() => {
                  onChange(code || name);
                  setOpen(false);
                }}
              >
                <Select.ItemLabel selected={code === value || name === value}>
                  {name}
                </Select.ItemLabel>
                {(code === value || name === value) && <Select.ItemCheck />}
              </Select.Item>
            ))
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
